/**
 * Utilidades para normalización y validación de series documentales.
 * Extraídas de DocumentSeriesAdmin.jsx (P3 #73).
 */

export const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'sí';
  }
  return false;
};

export const parseIntOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export const parseFloatOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

export const normalizeSeries = (serie) => ({
  ...serie,
  id: serie.id,
  name: serie.name ?? '',
  // Post #57: el API expone el folder padre como `parent`. Mantenemos null si
  // el backend lo deja null (serie en la raíz del doctree). El default histórico
  // `'ADMIN'` era válido cuando todas las series colgaban de un folder con ese
  // id, pero ya no aplica con árbol N-niveles flexible.
  parent: serie.parent ?? null,
  order: serie.order ?? 0,
  ocrRequired: toBoolean(serie.ocrRequired),
  reviewRequired: toBoolean(serie.reviewRequired),
  approvalRequired: toBoolean(serie.approvalRequired),
  signatureRequired: toBoolean(serie.signatureRequired),
  isSeries: toBoolean(serie.isSeries),
  state: serie.state,
});

/**
 * Normaliza un field spec del API al shape que espera el componente.
 * Post-rework #19: el API devuelve name/type/displayOrder pero la UI usa
 * nombre/tipo/posicion internamente. Este normalizer hace el bridge.
 * También infiere "origin" desde inputStrategies para la agrupación visual.
 */
export const normalizeFieldSpec = (field) => {
  let inferredOrigin = 'csv';
  if (field?.inputStrategies && field.inputStrategies.length > 0) {
    const strategyTypes = field.inputStrategies.map(s => s.strategyType || s.strategy_type);
    if (strategyTypes.includes('OCR_AI') && !strategyTypes.includes('CSV_POSITIONAL')) {
      inferredOrigin = 'ocr';
    } else if (strategyTypes.includes('CSV_POSITIONAL')) {
      inferredOrigin = 'csv';
    } else if (strategyTypes.includes('MANUAL')) {
      inferredOrigin = 'manual';
    }
  }

  let strategies = field.inputStrategies || [];
  if (strategies.length === 0 && field.origin) {
    const originLower = (field.origin || '').toLowerCase();
    if (originLower === 'csv') {
      strategies = [{ strategyType: 'CSV_POSITIONAL', config: { position: field.posicion || field.displayOrder || 1 } }];
    } else if (originLower === 'ocr') {
      strategies = [{ strategyType: 'OCR_AI', config: {} }];
    } else if (originLower === 'manual') {
      strategies = [{ strategyType: 'MANUAL', config: {} }];
    }
  }

  return {
    ...field,
    nombre: field.name || field.nombre,
    tipo: field.type || field.tipo,
    posicion: field.displayOrder || field.posicion,
    origin: field.origin || inferredOrigin,
    required: toBoolean(field?.required),
    mask: field?.mask ?? '',
    name: field.name || field.nombre,
    type: field.type || field.tipo,
    displayOrder: field.displayOrder || field.posicion,
    inputStrategies: strategies,
  };
};

/**
 * Política de nombrado de identificadores en Bóveda. Usada para IDs de series y nombres
 * de campos de metadata. Coincide con la validación del backend en
 * `ValidationUtil.validateIdentifier` (regex `^[a-zA-Z0-9_][a-zA-Z0-9_-]*$`, 50 chars).
 * Útil para mostrar la política como hint en los inputs, evitar drift entre regex
 * frontend/backend si cambia.
 */
export const IDENTIFIER_POLICY_HINT =
  'Solo letras, números, guion alto (-) y guion bajo (_). No puede empezar con guion alto. Sin espacios. Máx. 50 caracteres.';

export const IDENTIFIER_POLICY_REGEX = /^[a-zA-Z0-9_][a-zA-Z0-9_-]*$/;

/**
 * Valida un identificador genérico (ID de serie, name de campo, etc.) contra la política
 * compartida con el backend. Devuelve null si es válido o un mensaje de error.
 *
 * @param value - valor a validar
 * @param label - etiqueta humana del campo, ej. "ID de la serie", "nombre del campo"
 */
export const validateIdentifier = (value, label = 'identificador') => {
  if (!value || !value.trim()) {
    return `El ${label} es obligatorio`;
  }
  const trimmedValue = value.trim();
  if (trimmedValue.includes(' ') || trimmedValue.includes('\t')) {
    return `El ${label} no puede contener espacios ni tabs`;
  }
  if (!IDENTIFIER_POLICY_REGEX.test(trimmedValue)) {
    return `El ${label} solo puede contener letras, números, guiones altos y bajos, y no puede empezar con guion alto`;
  }
  if (trimmedValue.length > 50) {
    return `El ${label} no puede exceder los 50 caracteres`;
  }
  return null;
};

/**
 * Wrapper retrocompatible: validador específico del nombre de campo de metadata.
 * Mantiene la firma original (1 argumento) para no romper callers existentes en
 * DocumentSeriesAdmin y MetadataFieldsEditor.
 */
export const validateFieldIdentifier = (value) =>
  validateIdentifier(value, 'nombre del campo');
